import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import * as z from "zod";
import { invokeWithRetry } from "@/services/edgeFunctionWrappers/common/invokeWithRetry";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import type { CreateTenantRequest } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const formSchema = z.object({
  gymName: z.string().min(1, i18n.t("superadmin:createTenant.validation.gymNameRequired")),
  city: z.string().min(1, i18n.t("superadmin:createTenant.validation.cityRequired")),
  address: z.string().min(1, i18n.t("superadmin:createTenant.validation.addressRequired")),
  phone: z.string().min(1, i18n.t("superadmin:createTenant.validation.phoneRequired")),
  email: z.string().email(i18n.t("superadmin:createTenant.validation.emailInvalid")),
  adminEmail: z.string().email(i18n.t("superadmin:createTenant.validation.adminEmailInvalid")),
  adminFirstName: z.string().min(1, i18n.t("superadmin:createTenant.validation.adminFirstNameRequired")),
  adminLastName: z.string().min(1, i18n.t("superadmin:createTenant.validation.adminLastNameRequired")),
  adminPhone: z.string().optional(),
});

interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateTenantDialog({ open, onOpenChange, onSuccess }: CreateTenantDialogProps) {
  const { t } = useTranslation("superadmin");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<CreateTenantRequest>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      gymName: "",
      city: "",
      address: "",
      phone: "",
      email: "",
      adminEmail: "",
      adminFirstName: "",
      adminLastName: "",
      adminPhone: "",
    },
  });

  // Brouillon persisté — 9 champs texte, le formulaire le plus long de
  // l'app (Annuler efface, Escape/overlay conserve).
  const { clearDraft } = useFormDraft("create-tenant", form, {
    isEmpty: (v) => !v.gymName && !v.email && !v.adminEmail,
  });

  async function onSubmit(values: CreateTenantRequest) {
    setIsSubmitting(true);
    try {
      // invokeWithRetry (et non supabase.functions.invoke brut) : sur un refus
      // de l'edge, il lit le corps `{ error: "…" }` et pose le VRAI message sur
      // error.message — l'appel brut n'affichait que le générique de supabase-js
      // (« Edge Function returned a non-2xx status code »), illisible pour
      // l'utilisateur (cas vécu : « Admin email already exists »).
      const { data, error } = await invokeWithRetry('create-tenant', values);

      if (error) throw error;

      toast({
        title: t("createTenant.createdSuccess"),
        description: t("createTenant.createdSuccessDesc", { name: data.gym.name }),
      });

      clearDraft();
      form.reset();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating tenant:', error);
      const message: string = error?.message ?? "";
      const description = message.includes("already exists")
        ? t("createTenant.adminEmailExists")
        : message || t("createTenant.errorDesc");
      toast({
        title: t("createTenant.error"),
        description,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("createTenant.title")}</DialogTitle>
          <DialogDescription>
            {t("createTenant.description")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-4">
              <h3 className="font-semibold">{t("createTenant.gymInfo")}</h3>
              
              <FormField
                control={form.control}
                name="gymName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("createTenant.gymName")} *</FormLabel>
                    <FormControl>
                      <Input placeholder="VisionBody Tunisie" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("createTenant.city")} *</FormLabel>
                      <FormControl>
                        <Input placeholder="Tunis" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("createTenant.phone")} *</FormLabel>
                      <FormControl>
                        <Input placeholder="+216 XX XXX XXX" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("createTenant.address")} *</FormLabel>
                    <FormControl>
                      <Input placeholder="123 Rue..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("createTenant.gymEmail")} *</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="contact@gym.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">{t("createTenant.adminAccount")}</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="adminFirstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("createTenant.firstName")} *</FormLabel>
                      <FormControl>
                        <Input placeholder="Mohamed" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="adminLastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("createTenant.lastName")} *</FormLabel>
                      <FormControl>
                        <Input placeholder="Ben Ali" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="adminEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("createTenant.adminEmail")} *</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="admin@gym.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="adminPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("createTenant.adminPhone")}</FormLabel>
                    <FormControl>
                      <Input placeholder="+216 XX XXX XXX" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              {/* Annulation explicite : le brouillon est jeté. */}
              <Button
                type="button"
                variant="outline"
                onClick={() => { clearDraft(); onOpenChange(false); }}
                disabled={isSubmitting}
              >
                {t("createTenant.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("createTenant.submit")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
